import {type FC, memo, useCallback, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import classNames from 'classnames';
import dayjs from 'dayjs';

import type {FileApiModel} from '@yourcoach/shared/api/media/file';
import type {LinkApiModel as Link} from '@yourcoach/shared/api/media/link';

import {useI18n} from '@yourcoach/i18n';
import {createFileNotFound} from '@yourcoach/shared/utils/entity/file';

import type {ScheduleMaterial} from '@src/modules/materials/utils';

import Button from '@src/components/Button';
import FileCard from '@src/modules/library/FileCard';
import FolderCard from '@src/modules/library/FolderCard';
import {getMaterialFile, materialIsFolder} from '@src/modules/materials/utils';

import styles from './LibraryListItem.module.css';

interface Props {
  className?: string;
  material: ScheduleMaterial;
  onClick?: (item: ScheduleMaterial) => void;
  onMoreButtonClick?: (
    item: ScheduleMaterial,
    event: React.MouseEvent<Element, MouseEvent>,
  ) => void;
  onPublicationDayButtonClick?: (item: ScheduleMaterial) => void;
}

const CRUProgramLibraryListItem: FC<Props> = props => {
  const {t} = useI18n();
  const {
    className,
    material,
    onClick,
    onMoreButtonClick,
    onPublicationDayButtonClick,
  } = props;

  const onClickCb = useCallback(() => {
    onClick?.(material);
  }, [onClick, material]);

  const onMoreButtonClickCb = useCallback(
    (_item: FileApiModel | Link, e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();

      onMoreButtonClick?.(material, e);
    },
    [material, onMoreButtonClick],
  );

  const onPublicationDayButtonPressCb = useCallback(() => {
    onPublicationDayButtonClick?.(material);
  }, [material, onPublicationDayButtonClick]);

  const file = useMemo(
    () => getMaterialFile(material) || createFileNotFound(),
    [material],
  );

  const folder = useMemo(
    () => ({
      name: material.title,
      ...material,
    }),
    [material],
  );

  const isFolder = useMemo(() => materialIsFolder(material), [material]);

  const time = useMemo(
    () => dayjs().startOf('day').add(material.time, 'second').format('LT'),
    [material.time],
  );

  const listItem = useMemo(() => {
    if (isFolder) {
      return (
        <FolderCard
          className={styles.card}
          // @ts-expect-error: TODO: Fix this
          folder={folder}
          onClick={onClickCb}
          onMoreButtonClick={
            onMoreButtonClick ? onMoreButtonClickCb : undefined
          }
        />
      );
    } else {
      return (
        <FileCard
          className={styles.card}
          file={file}
          onClick={onClickCb}
          onMoreButtonClick={
            onMoreButtonClick ? onMoreButtonClickCb : undefined
          }
        />
      );
    }
  }, [
    file,
    folder,
    isFolder,
    onClickCb,
    onMoreButtonClick,
    onMoreButtonClickCb,
  ]);

  return (
    <div
      className={classNames(styles.container, className)}
      onClick={onClickCb}
    >
      {listItem}
      <Button
        className={styles.publicationDayButton}
        onClick={onPublicationDayButtonPressCb}
      >
        {material.day === 0
          ? t('shared:CRUProgramLibraryListItem.first_day_label')
          : t('shared:CRUProgramLibraryListItem.day_label', {
              day: material.day + 1,
            })}
        {`, ${time}`}
      </Button>
    </div>
  );
};

export default memo(CRUProgramLibraryListItem, isEqual);
